import React, { useState } from 'react';
import { configure, getApplicationKeyMap, GlobalHotKeys } from 'react-hotkeys';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@sumologic/yama-ds-components';

import css from './HotkeyDialog.module.scss';

/* We currently call react-hotkeys.Configure in one other place.
 * When making changes to the configuration of react-hotKeys, please make sure that
 * the configuration done in https://github.com/jasklabs/fleet/blob/master/views/Nav/src/GoTo/GoToContent.tsx isn't being overridden.
 * TODO: @anu-sumo make sure configure is only called in one place after kanso GA
 */
configure({
  ignoreEventsCondition: (e: KeyboardEvent) => {
    if (
      e.target instanceof HTMLElement &&
      e.target.id === 'sumo-go-to-search-input'
    ) {
      return false;
    }

    if (e.altKey || e.metaKey || e.ctrlKey) {
      // Reference: https://github.com/greena13/react-hotkeys/issues/234#issuecomment-538001235
      return true;
    }

    // Reference: https://github.com/greena13/react-hotkeys#configuration
    if (event?.target) {
      const target = event.target as HTMLElement;
      const tagName = target.tagName?.toLowerCase();
      return (
        ['textarea', 'input', 'select'].includes(tagName) ||
        target.isContentEditable
      );
    }

    return false;
  },
});

export const HotkeyDialog = ({ children }) => {
  const [visible, setVisible] = useState<boolean>(false);
  const keys = getApplicationKeyMap() || [];

  return (
    <GlobalHotKeys
      handlers={{
        SHOW: () => setVisible(true),
      }}
      keyMap={{
        SHOW: {
          name: 'Brings up this help dialog',
          sequence: 'shift+?',
          action: 'keydown',
        },
      }}
    >
      <Dialog
        className={css.hotkeyDialog}
        data-sumo-test='hotkey-dialog'
        onClose={() => setVisible(false)}
        open={visible}
      >
        <DialogTitle>Keyboard Shortcuts on this page</DialogTitle>
        {visible && (
          <>
            <DialogContent className={css.hotkeyList} role='list'>
              {Object.keys(keys).map((k) => (
                <div className={css.hotkeyListItem} key={k} role='listitem'>
                  <span
                    className={css.name}
                    data-sumo-test={`hotkey-dialog-item-name`}
                  >
                    {keys[k].name || k}
                  </span>
                  {keys[k].description && (
                    <span className={css.description}>
                      {keys[k].description}
                    </span>
                  )}
                  {keys[k].sequences.map((kk, i) => (
                    <span className={css.keyCombo} key={i}>
                      {kk.sequence}
                    </span>
                  ))}
                </div>
              ))}
            </DialogContent>
            <DialogActions>
              <Button color='primary' onClick={() => setVisible(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {children}
    </GlobalHotKeys>
  );
};
